import React from "react";

const MatrixSkeletonLoader: React.FC = () => {
  return (
    <div className="h-full bg-white rounded-lg">
      <div className="max-w-6xl mx-auto min-h-full px-4">
        {/* Matrix Header Skeleton */}
        <div className="w-full">
          <div className="bg-white px-4 py-3 flex justify-between shadow-md">
            {/* Matrix Title */}
            <div className="text-gray-800 font-semibold text-lg">
              <div className="w-16 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>

            {/* Right side - Create buttons and Profile Icon */}
            <div className="flex items-center gap-4">
              <div className="flex items-center text-gray-400 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center text-white bg-gray-800 py-2 px-3 gap-2 border border-gray-200 rounded-lg">
                <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-[1px] h-6 bg-gray-200"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="overflow-auto">
          {/* Search Bar, Filter Button, and View Mode Buttons */}
          <div className="flex gap-2 items-center mt-4 mb-2">
            <div className="relative flex-1 p-1">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-full pl-10 pr-4 h-10 border border-gray-200 rounded-lg bg-gray-100 animate-pulse"></div>
            </div>
            {/* View Mode Buttons */}
            <div className="flex gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-gray-50 border-gray-200">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-blue-50 border-blue-200">
                <div className="w-4 h-4 bg-blue-200 rounded animate-pulse"></div>
              </div>
            </div>
            {/* Filter Button */}
            <div className="relative">
              <div className="flex items-center gap-2 w-10 h-10 justify-center rounded-lg border bg-gray-50 border-gray-200">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Number of results text */}
          <div className="my-4 text-lg text-gray-600 font-semibold">
            <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Table View Skeleton */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
            {/* Table Header */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                <div className="col-span-6">
                  <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-3">
                  <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-2">
                  <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="col-span-1"></div>
              </div>
            </div>

            {/* Table Body */}
            <div className="space-y-0">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="px-6 py-2 border-b border-gray-100">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Template Name */}
                    <div className="col-span-6">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-1">
                        <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-span-2">
                      <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex justify-end">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Matrix Editor Skeleton Loader
export const MatrixEditorSkeletonLoader: React.FC = () => {
  return (
    <div className="bg-white flex flex-col h-full w-full rounded-lg overflow-hidden">
      {/* Matrix Editor Header Skeleton */}
      {/* z-20, page chrome: the nav's hover panel (z-30, Layout) paints over it (#12209). */}
      <div className="absolute top-0 left-0 right-0 z-20">
        <div className="bg-white px-4 py-3 flex justify-between shadow-md">
          {/* Matrix Title */}
          <div className="text-gray-800 font-semibold text-lg">
            <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>

          {/* Right side - Share button and Profile Icon */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="w-[1px] h-6 bg-gray-200"></div>
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Matrix Editor Content */}
      <div className="flex-1 overflow-auto pt-16">
        <div className="flex h-full">
          {/* Left Panel - Setup/Configuration */}
          <div className="w-2/5 border-r border-gray-200 p-4">
            <div className="space-y-4">
              {/* Setup Wizard Skeleton */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
              </div>

              {/* Configuration Options */}
              <div className="space-y-3">
                <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Matrix Grid */}
          <div className="w-3/5 p-4">
            <div className="bg-gray-50 rounded-lg p-4 h-full">
              {/* Grid Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* Grid Content */}
              <div className="grid grid-cols-3 gap-4">
                {[...Array(9)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg p-3 border border-gray-200"
                  >
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatrixSkeletonLoader;
